import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { candidateApi, candidateAuthApi, SessionTimeoutError } from '../lib/candidate-api';
import type { 
  Applicant,
  MagicLinkRequest,
  MagicLinkVerifyRequest
} from '../types/auth';

// Candidate Auth State
interface CandidateAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  applicant: Applicant | null;
  error: string | null;
  isSessionExpired: boolean;
}

// Candidate Auth Context Type
interface CandidateAuthContextType extends CandidateAuthState {
  sendMagicLink: (email: string, companyId?: string) => Promise<{
    success: boolean;
    message: string;
    error?: string;
  }>;
  verifyMagicLink: (token: string) => Promise<{
    success: boolean;
    message: string;
    error?: string;
  }>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshSession: () => Promise<void>;
  handleSessionTimeout: () => void;
  clearSessionTimeout: () => void;
}

// Auth State Actions
type CandidateAuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_AUTHENTICATED'; payload: { applicant: Applicant } }
  | { type: 'SET_UNAUTHENTICATED' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_SESSION_EXPIRED'; payload: boolean };

// Initial state
const initialState: CandidateAuthState = {
  isAuthenticated: false,
  isLoading: true, // Start as loading to check existing session
  applicant: null,
  error: null,
  isSessionExpired: false,
};

// Auth reducer
function candidateAuthReducer(state: CandidateAuthState, action: CandidateAuthAction): CandidateAuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_AUTHENTICATED':
      return {
        ...state,
        isAuthenticated: true,
        applicant: action.payload.applicant,
        error: null,
        isLoading: false,
        isSessionExpired: false,
      };
    case 'SET_UNAUTHENTICATED':
      return {
        ...state,
        isAuthenticated: false,
        applicant: null,
        error: null,
        isLoading: false,
        isSessionExpired: false,
      };
    case 'SET_SESSION_EXPIRED':
      return {
        ...state,
        isSessionExpired: action.payload,
        isAuthenticated: !action.payload,
        isLoading: false, // Set loading to false when session expires
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

// Create context
const CandidateAuthContext = createContext<CandidateAuthContextType | undefined>(undefined);

// Candidate Auth Provider Component
interface CandidateAuthProviderProps {
  children: React.ReactNode;
}

export function CandidateAuthProvider({ children }: CandidateAuthProviderProps) {
  const [state, dispatch] = useReducer(candidateAuthReducer, initialState);

  // Session timeout handler
  const handleSessionTimeout = useCallback(() => {
    dispatch({ type: 'SET_SESSION_EXPIRED', payload: true });
  }, []);

  // Clear session timeout state
  const clearSessionTimeout = useCallback(() => {
    dispatch({ type: 'SET_SESSION_EXPIRED', payload: false });
  }, []);

  // Check existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      // Skip candidate session check if we're on admin routes
      const currentPath = window.location.pathname;
      const isAdminRoute = currentPath.startsWith('/admin') || currentPath.startsWith('/auth/hr-login');
      
      if (isAdminRoute) {
        dispatch({ type: 'SET_UNAUTHENTICATED' });
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        // Only check applicant session for candidate auth
        const response = await candidateAuthApi.getSession();
        if ('success' in response && response.success) {
          // Store session token in sessionStorage for Authorization header fallback
          // This ensures the token persists across page reloads even if cookies fail
          if (response.sessionToken) {
            sessionStorage.setItem('candidate_session_token', response.sessionToken);
          }

          dispatch({
            type: 'SET_AUTHENTICATED',
            payload: { applicant: response.applicant }
          });
        } else {
          dispatch({ type: 'SET_UNAUTHENTICATED' });
        }
      } catch (error) {
        // Handle session timeout errors specifically
        if (error instanceof SessionTimeoutError) {
          handleSessionTimeout();
        } else {
          dispatch({ type: 'SET_UNAUTHENTICATED' });
        }
      }
    };

    // Set up session timeout handler for API client
    candidateApi.setSessionTimeoutHandler(handleSessionTimeout);

    checkSession();
  }, [handleSessionTimeout]);

  // Send magic link
  const sendMagicLink = useCallback(async (email: string, companyId?: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const request: MagicLinkRequest = { email, companyId };
      const response = await candidateAuthApi.sendMagicLink(request);

      if ('success' in response && response.success) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { 
          success: true, 
          message: response.message 
        };
      } else {
        const error = 'error' in response ? response.error : 'Failed to send magic link';
        dispatch({ type: 'SET_ERROR', payload: error });
        return { 
          success: false, 
          message: 'Failed to send magic link', 
          error 
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return { 
        success: false, 
        message: 'Failed to send magic link', 
        error: errorMessage 
      };
    }
  }, []);

  // Verify magic link
  const verifyMagicLink = useCallback(async (token: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const request: MagicLinkVerifyRequest = { token };
      const response = await candidateAuthApi.verifyMagicLink(request);

      if ('success' in response && response.success && response.applicant) {
        // Store session token in sessionStorage as backup for cookie-based auth
        // This helps with proxy/cookie issues in development
        if (response.sessionToken) {
          sessionStorage.setItem('candidate_session_token', response.sessionToken);
        }

        dispatch({
          type: 'SET_AUTHENTICATED',
          payload: {
            applicant: response.applicant
          }
        });
        return {
          success: true,
          message: response.message
        };
      } else {
        const error = 'error' in response ? response.error : 'Failed to verify magic link';
        dispatch({ type: 'SET_ERROR', payload: error });
        return { 
          success: false, 
          message: 'Failed to verify magic link', 
          error 
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return { 
        success: false, 
        message: 'Failed to verify magic link', 
        error: errorMessage 
      };
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await candidateAuthApi.logout();
    } catch (error) {
      // Continue with local logout even if API call fails
    }
    // Clear session token from sessionStorage
    sessionStorage.removeItem('candidate_session_token');
    dispatch({ type: 'SET_UNAUTHENTICATED' });
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      // Temporarily disable session timeout handler to prevent recursive calls
      candidateApi.setSessionTimeoutHandler(() => {});
      
      const response = await candidateAuthApi.getSession();
      if ('success' in response && response.success) {
        dispatch({ 
          type: 'SET_AUTHENTICATED', 
          payload: { applicant: response.applicant } 
        });
      } else {
        throw new Error('Session refresh failed');
      }
      
      // Restore the original session timeout handler
      candidateApi.setSessionTimeoutHandler(handleSessionTimeout);
    } catch (error) {
      // Restore the original session timeout handler
      candidateApi.setSessionTimeoutHandler(handleSessionTimeout);
      throw error; // Re-throw to let the dialog handle the failure
    }
  }, [handleSessionTimeout]);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const contextValue: CandidateAuthContextType = {
    ...state,
    sendMagicLink,
    verifyMagicLink,
    logout,
    clearError,
    refreshSession,
    handleSessionTimeout,
    clearSessionTimeout,
  };

  return (
    <CandidateAuthContext.Provider value={contextValue}>
      {children}
    </CandidateAuthContext.Provider>
  );
}

// Custom hook to use candidate auth context
export function useCandidateAuth(): CandidateAuthContextType {
  const context = useContext(CandidateAuthContext);
  if (context === undefined) {
    throw new Error('useCandidateAuth must be used within a CandidateAuthProvider');
  }
  return context;
}

// Hook to check if candidate is authenticated
export function useCandidateAuthGuard(): { isAuthenticated: boolean; isLoading: boolean } {
  const { isAuthenticated, isLoading } = useCandidateAuth();
  return { isAuthenticated, isLoading };
}